import dayjs from 'dayjs'
import { exportToExcel } from '@/utils/exportUtils'
import { formatConsumptionValue } from '@/utils/consumptionFormat'
import { Box, Tab, Tabs, Snackbar, Typography, LinearProgress } from '@mui/material'
import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import ConsumerCallsContent from './ConsumerCallsContent'
import { filtersTypes } from './types'
import { useParams, useOutletContext } from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'
import { getHourlyReadings, HourlyReadingRow, getDailyReadings, DailyReadingRow, getPeriodicReadings, PeriodicReadingRow, PeriodOption, getDetailedReadings, DetailedReadingRow, READINGS_FETCH_FAILED, type DailyReadingsPage, type HourlyReadingsPage } from '@/services/consumerService'
import { getTablePageSize } from '@/hooks/useTablePageSize'
import { useConsumerPremise } from '@/hooks/useConsumerPremise'
import {
  formatReadingsDate,
  getDefaultRange,
  getDefaultFifteenthDateRange,
} from '@/utils/consumerReadingsDateRanges'
import {
  describeGatewayError,
  type GatewayError,
  type GatewayResult,
} from '@/utils/gatewayError'
import PageStateMessage from '@/components/common/PageStateMessage'
import { debugLog } from '@/utils/debugLog'

const parseApiDate = (value?: string) => value ? new Date(`${value}T00:00:00`) : null
type ConsumerDateRange = { from: Date | null; to: Date | null }

const ConsumerCalls = () => {
  const { consumerId } = useParams() // This is actually meterNumber from URL
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [pageSize, setPageSize] = useState(() => getTablePageSize(50))

  // Export progress state
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  // Get accountId from localStorage
  const accountId = localStorage.getItem('accountId') || '46'

  // The readings tabs resolve the premise themselves from the meter number in
  // the URL, so a failure on the details tab cannot keep them loading forever.
  const premise = useConsumerPremise(accountId, consumerId)
  const premiseIdRef = useRef<number | null>(null)
  premiseIdRef.current = premise.premiseId
  const premiseIdReady = premise.status === 'ready'
  // Keep the grid's own loading overlay up while the premise resolves, so the
  // tab never shows an empty table for a request it has not sent yet.
  const premiseLoading = premise.status === 'loading'
  const [hourlyData, setHourlyData] = useState<HourlyReadingRow[]>([])
  const [hourlyLoading, setHourlyLoading] = useState(false)
  const [hourlyTotal, setHourlyTotal] = useState(0)
  const [hourlyAvg, setHourlyAvg] = useState(0)
  const [hourlyRowCount, setHourlyRowCount] = useState(0)
  const [hourlyPage, setHourlyPage] = useState(0)
  const [hourlyError, setHourlyError] = useState<GatewayError | null>(null)
  const [dailyData, setDailyData] = useState<DailyReadingRow[]>([])
  const [dailyLoading, setDailyLoading] = useState(false)
  const [dailyChartData, setDailyChartData] = useState<DailyReadingRow[]>([])
  const [dailyChartLoading, setDailyChartLoading] = useState(false)
  const [dailyChartError, setDailyChartError] = useState<GatewayError | null>(null)
  const dailyChartRequestRef = useRef(0)
  const [dailyTotal, setDailyTotal] = useState(0)
  const [dailyAvg, setDailyAvg] = useState(0)
  const [dailyNightlyTotal, setDailyNightlyTotal] = useState(0)
  const [dailyNightlyAvg, setDailyNightlyAvg] = useState(0)
  const [dailyRowCount, setDailyRowCount] = useState(0)
  const [dailyPage, setDailyPage] = useState(0)
  const [dailyError, setDailyError] = useState<GatewayError | null>(null)
  // Min/Max with page info and pre-loaded rows for jump functionality
  const [dailyMinInfo, setDailyMinInfo] = useState<{ value: number; day: string; pageNumber: number; totalPages: number; totalElements: number; pageRows: any[] | null } | null>(null)
  const [dailyMaxInfo, setDailyMaxInfo] = useState<{ value: number; day: string; pageNumber: number; totalPages: number; totalElements: number; pageRows: any[] | null } | null>(null)
  // Periodic data state
  const [periodicData, setPeriodicData] = useState<PeriodicReadingRow[]>([])
  const [periodicLoading, setPeriodicLoading] = useState(false)
  const [periodicTotal, setPeriodicTotal] = useState(0)
  const [periodicAvg, setPeriodicAvg] = useState(0)
  const [periodicChart, setPeriodicChart] = useState<{ currentYear: number[]; yearAgo: number[] | null }>({ currentYear: [], yearAgo: null })
  const [periodicRowCount, setPeriodicRowCount] = useState(0)
  const [periodicPage, setPeriodicPage] = useState(0)
  // Detailed data state
  const [detailedData, setDetailedData] = useState<DetailedReadingRow[]>([])
  const [detailedLoading, setDetailedLoading] = useState(false)
  const [detailedRowCount, setDetailedRowCount] = useState(0)
  const [detailedPage, setDetailedPage] = useState(0)
  const [value, setValue] = useState(1)

  // Default backend sortBy field per tab type
  const defaultSortByMap: Record<string, string> = {
    [filtersTypes.hourly]: 'hourly_time',
    [filtersTypes.daily]: 'daily_day',
    [filtersTypes.periodic]: 'period_start',
    [filtersTypes.detailed]: 'detailed_reading_at',
  }

  // Default backend sortOrder per tab type (matches controller defaults)
  const defaultSortOrderMap: Record<string, 'ASC' | 'DESC'> = {
    [filtersTypes.hourly]: 'DESC',
    [filtersTypes.daily]: 'DESC',
    [filtersTypes.periodic]: 'DESC',
    [filtersTypes.detailed]: 'DESC',
  }

  // Sort state — initialized with correct backend defaults for first tab (hourly)
  const [sortBy, setSortBy] = useState<string>('daily_day')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  const [activeSortField, setActiveSortField] = useState<string>('timestamp')

  const [hourlyRange, setHourlyRange] = useState<ConsumerDateRange>(() => getDefaultRange(3, 0))
  const [dailyRange, setDailyRange] = useState<ConsumerDateRange>(() => getDefaultRange(0, 30))
  const [periodicRange, setPeriodicRange] = useState<ConsumerDateRange>(() => getDefaultFifteenthDateRange(1))
  const [detailedRange, setDetailedRange] = useState<ConsumerDateRange>(() => getDefaultRange(0, 7))
  const [periodicOption, setPeriodicOption] = useState<PeriodOption>('BI_MONTHLY')

  const handleChange = (_: unknown, newValue: number) => {
    setValue(newValue)
  }

  const options: { label: string; value: number; type: filtersTypes }[] = [
    { label: 'שעתי', value: 0, type: filtersTypes.hourly },
    { label: 'יומי', value: 1, type: filtersTypes.daily },
    { label: 'תקופתי', value: 2, type: filtersTypes.periodic },
    { label: 'מפורט', value: 3, type: filtersTypes.detailed },
  ]

  // Get outlet context to register the Excel export handler with the parent layout
  const { setExcelExportHandler } = useOutletContext<{ setExcelExportHandler: (fn: (() => void) | null) => void }>() ?? {}

const DAYS_OF_WEEK = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש']

const getReadingsExportColumns = (type: filtersTypes) => {
  if (type === filtersTypes.hourly) {
    return [
      { field: 'meterNumber', headerName: "מס' מד" },
      { field: 'timestamp', headerName: 'תאריך ושעה' },
      { field: 'value', headerName: 'קריאה לתחילת שעה' },
      { field: 'consumption', headerName: 'צריכה שעתית' },
      { field: 'alerts', headerName: 'התראה' },
    ]
  }

  if (type === filtersTypes.daily) {
    return [
      { field: 'meterNumber', headerName: "מס' מד" },
      { field: 'timestamp', headerName: 'תאריך' },
      { field: 'dayOfWeek', headerName: 'יום' },
      { field: 'midnightCapacity', headerName: 'ק. חצות' },
      { field: 'consumption', headerName: 'צ.יומית' },
      { field: 'nightConsumption', headerName: 'צ.לילה' },
      { field: 'alerts', headerName: 'התראה' },
    ]
  }

  if (type === filtersTypes.periodic) {
    return [
      { field: 'meterNumber', headerName: "מס' מד" },
      { field: 'periodRange', headerName: 'תקופה' },
      { field: 'startReading', headerName: 'קריאה בתחילה' },
      { field: 'endReading', headerName: 'קריאה בסיום' },
      { field: 'consumption', headerName: 'צריכה תקופתית' },
    ]
  }

  if (type === filtersTypes.detailed) {
    return [
      { field: 'meterNumber', headerName: "מס' מד" },
      { field: 'timestamp', headerName: 'תאריך קליטה' },
      { field: 'value', headerName: 'קריאה אחרונה' },
      { field: 'reverse', headerName: 'קריאה חוזרת' },
    ]
  }

  return []
}

const formatReadingsRowsForExport = (type: filtersTypes, rawRows: any[]) => {
  if (!Array.isArray(rawRows)) return []

  if (type === filtersTypes.hourly) {
    return rawRows.map(row => {
      const rawAlert = row.alerts || row.alert
      const alertsStr = Array.isArray(rawAlert)
        ? rawAlert.join(', ')
        : (rawAlert || '-')
      return {
        meterNumber: row.meterNumber ?? '-',
        timestamp: row.timestamp ? dayjs(row.timestamp).format('DD/MM/YYYY HH:mm') : '-',
        value: formatConsumptionValue(row.value ?? row.startReading),
        consumption: formatConsumptionValue(row.consumption),
        alerts: alertsStr,
      }
    })
  }

  if (type === filtersTypes.daily) {
    return rawRows.map(row => {
      const dateVal = row.timestamp ?? row.date
      const dayStr = row.dayOfWeek || (dateVal ? DAYS_OF_WEEK[dayjs(dateVal).day()] : '-')
      const rawAlert = row.alerts || row.alert
      const alertsStr = Array.isArray(rawAlert)
        ? rawAlert.join(', ')
        : (rawAlert || '-')

      return {
        meterNumber: row.meterNumber ?? '-',
        timestamp: dateVal ? dayjs(dateVal).format('DD/MM/YYYY') : '-',
        dayOfWeek: dayStr,
        midnightCapacity: formatConsumptionValue(row.midnightCapacity),
        consumption: formatConsumptionValue(row.consumption),
        nightConsumption: formatConsumptionValue(row.nightConsumption),
        alerts: alertsStr,
      }
    })
  }

  if (type === filtersTypes.periodic) {
    return rawRows.map(row => ({
      meterNumber: row.meterNumber ?? '-',
      periodRange: row.periodRange || row.period || row.periodStart || '-',
      startReading: formatConsumptionValue(row.startReading ?? row.start_reading),
      endReading: formatConsumptionValue(row.endReading ?? row.end_reading),
      consumption: formatConsumptionValue(row.consumption),
    }))
  }

  if (type === filtersTypes.detailed) {
    return rawRows.map(row => ({
      meterNumber: row.meterNumber ?? '-',
      timestamp: row.timestamp ? dayjs(row.timestamp).format('DD/MM/YYYY HH:mm:ss') : '-',
      value: formatConsumptionValue(row.value),
      reverse: formatConsumptionValue(row.reverse),
    }))
  }

  return rawRows
}

  // Excel export handler — exports the exact columns and rows shown on the website
  const handleExcelExport = useCallback(async () => {
    if (isExporting) return

    setIsExporting(true)
    setExportProgress(10)
    stopProgressTimer()

    let simulated = 10
    progressTimerRef.current = setInterval(() => {
      simulated += (90 - simulated) * 0.1
      setExportProgress((prev) => Math.min(Math.round(Math.max(prev, simulated)), 90))
    }, 200)

    const currentType = options[value].type
    const cols = getReadingsExportColumns(currentType)

    try {
      const premiseId = premiseIdRef.current

      let rawRowsToExport: any[] = []

      if (premiseId) {
        if (currentType === filtersTypes.hourly) {
          const fetchSize = Math.max(hourlyRowCount, 100)
          const res = await getHourlyReadings(accountId, premiseId, {
            page: 1,
            size: fetchSize,
            from: formatReadingsDate(hourlyRange.from),
            to: formatReadingsDate(hourlyRange.to),
            sortBy,
            sortOrder,
          })
          rawRowsToExport = res.ok && res.data.rows.length > 0 ? res.data.rows : hourlyData
        } else if (currentType === filtersTypes.daily) {
          const fetchSize = Math.max(dailyRowCount, 100)
          const res = await getDailyReadings(accountId, premiseId, {
            page: 1,
            size: fetchSize,
            from: formatReadingsDate(dailyRange.from),
            to: formatReadingsDate(dailyRange.to),
            sortBy,
            sortOrder,
          })
          rawRowsToExport = res.ok && res.data.rows.length > 0 ? res.data.rows : dailyData
        } else if (currentType === filtersTypes.periodic) {
          const fetchSize = Math.max(periodicRowCount, 100)
          const res = await getPeriodicReadings(accountId, premiseId, {
            page: 1,
            size: fetchSize,
            from: formatReadingsDate(periodicRange.from),
            to: formatReadingsDate(periodicRange.to),
            periodOption: periodicOption,
            sortBy,
            sortOrder,
            meterNumber: consumerId,
          })
          rawRowsToExport = res.rows.length > 0 ? res.rows : periodicData
        } else if (currentType === filtersTypes.detailed) {
          const fetchSize = Math.max(detailedRowCount, 100)
          const res = await getDetailedReadings(accountId, premiseId, {
            page: 1,
            size: fetchSize,
            from: formatReadingsDate(detailedRange.from),
            to: formatReadingsDate(detailedRange.to),
            sortBy,
            sortOrder,
          })
          rawRowsToExport = res.rows.length > 0 ? res.rows : detailedData
        }
      }

      // If network fetch produced no rows, fallback to currently loaded table rows
      if (rawRowsToExport.length === 0) {
        rawRowsToExport =
          currentType === filtersTypes.daily
            ? dailyData
            : currentType === filtersTypes.hourly
            ? hourlyData
            : currentType === filtersTypes.periodic
            ? periodicData
            : detailedData
      }

      const formattedRows = formatReadingsRowsForExport(currentType, rawRowsToExport)
      const fileName = `consumer-${consumerId ?? 'meter'}-${currentType}-readings.xlsx`

      exportToExcel(cols, formattedRows, fileName)

      stopProgressTimer()
      setExportProgress(100)
      await new Promise(resolve => setTimeout(resolve, 300))
    } catch (err) {
      console.error('Excel export failed:', err)
    } finally {
      stopProgressTimer()
      setIsExporting(false)
      setExportProgress(0)
    }
  }, [value, accountId, consumerId, sortBy, sortOrder, hourlyRange, dailyRange, periodicRange, periodicOption, detailedRange, hourlyRowCount, hourlyData, dailyRowCount, dailyData, periodicRowCount, periodicData, detailedRowCount, detailedData, stopProgressTimer, isExporting])

  const handleExcelExportRef = useRef(handleExcelExport)
  handleExcelExportRef.current = handleExcelExport

  // Register the export handler with the parent layout
  useEffect(() => {
    if (setExcelExportHandler) {
      const stableHandler = () => handleExcelExportRef.current()
      setExcelExportHandler(() => stableHandler)
      return () => setExcelExportHandler(null)
    }
  }, [setExcelExportHandler])

  const showHourlyResult = useCallback((result: GatewayResult<HourlyReadingsPage>, page: number) => {
    setHourlyError(result.ok ? null : result.error)
    if (!result.ok) return

    setHourlyData(result.data.rows)
    setHourlyRowCount(result.data.total)
    setHourlyPage(page - 1) // DataGrid uses 0-based pages

    // Use backend-calculated total and average (across all records, not just current page)
    setHourlyTotal(Number(result.data.totalConsumption.toFixed(3)))
    setHourlyAvg(Number(result.data.avgConsumption.toFixed(3)))
  }, [])

  const showDailyResult = useCallback((result: GatewayResult<DailyReadingsPage>, page: number) => {
    setDailyError(result.ok ? null : result.error)
    if (!result.ok) return

    setDailyData(result.data.rows)
    setDailyRowCount(result.data.total)
    setDailyPage(page - 1) // DataGrid uses 0-based pages
    setDailyTotal(result.data.totalConsumption)
    setDailyAvg(result.data.avgConsumption)
    setDailyNightlyTotal(result.data.totalNightlyConsumption)
    setDailyNightlyAvg(result.data.avgNightlyConsumption)
    setDailyMinInfo(result.data.dailyMin)
    setDailyMaxInfo(result.data.dailyMax)
  }, [])

  // Fetch hourly data from API using premiseId (joins through premise_meters)
  const fetchHourlyData = useCallback(async (from?: string, to?: string, page: number = 1, sizeOverride?: number, sortByOverride?: string, sortOrderOverride?: 'ASC' | 'DESC') => {
    const idForApi = premiseIdRef.current
    if (!idForApi) return

    setHourlyLoading(true)
    try {
      const result = await getHourlyReadings(accountId, idForApi, {
        page,
        size: sizeOverride ?? pageSize,
        from: from ?? formatReadingsDate(hourlyRange.from),
        to: to ?? formatReadingsDate(hourlyRange.to),
        sortBy: getValidSortBy(filtersTypes.hourly, sortByOverride ?? sortBy),
        sortOrder: sortOrderOverride ?? sortOrder,
      })

      debugLog('[ConsumerCalls] Hourly API response:', result)
      showHourlyResult(result, page)
    } finally {
      setHourlyLoading(false)
    }
  }, [accountId, consumerId, pageSize, sortBy, sortOrder, hourlyRange, showHourlyResult])

  // Fetch daily data from API using premiseId (joins through premise_meters)
  const fetchDailyData = useCallback(async (from?: string, to?: string, page: number = 1, sizeOverride?: number, sortByOverride?: string, sortOrderOverride?: 'ASC' | 'DESC') => {
    const idForApi = premiseIdRef.current
    if (!idForApi) return

    setDailyLoading(true)
    try {
      const result = await getDailyReadings(accountId, idForApi, {
        page,
        size: sizeOverride ?? pageSize,
        from: from ?? formatReadingsDate(dailyRange.from),
        to: to ?? formatReadingsDate(dailyRange.to),
        sortBy: getValidSortBy(filtersTypes.daily, sortByOverride ?? sortBy),
        sortOrder: sortOrderOverride ?? sortOrder,
      })

      debugLog('[ConsumerCalls] Daily API response:', result)
      showDailyResult(result, page)
    } finally {
      setDailyLoading(false)
    }
  }, [accountId, consumerId, pageSize, sortBy, sortOrder, dailyRange, showDailyResult])


  // The weekly chart has its own backend request so navigation is independent
  // from the currently visible table page.
  const fetchDailyChartWeek = useCallback(async (from: string, to: string) => {
    const idForApi = premiseIdRef.current
    if (!idForApi || !premiseIdReady) return

    const requestId = ++dailyChartRequestRef.current
    setDailyChartLoading(true)
    try {
      const result = await getDailyReadings(accountId, idForApi, {
        page: 1,
        size: 50,
        from,
        to,
        sortBy: 'daily_day',
        sortOrder: 'ASC',
      })

      if (requestId === dailyChartRequestRef.current) {
        setDailyChartData(result.ok ? result.data.rows : [])
        setDailyChartError(result.ok ? null : result.error)
      }
    } finally {
      if (requestId === dailyChartRequestRef.current) {
        setDailyChartLoading(false)
      }
    }
  }, [accountId, premiseIdReady])

  // Auto-fetch hourly data when on hourly tab (tab 0)
  useEffect(() => {
    if (value === 0 && premiseIdReady && hourlyRange.from && hourlyRange.to) {
      const from = formatReadingsDate(hourlyRange.from)
      const to = formatReadingsDate(hourlyRange.to)
      const defSort = defaultSortByMap[filtersTypes.hourly]
      const defOrder = defaultSortOrderMap[filtersTypes.hourly]
      fetchHourlyData(from, to, 1, undefined, defSort, defOrder)
    }
  }, [value, premiseIdReady])

  // Auto-fetch daily data when switching to daily tab (tab 1)
  useEffect(() => {
    if (value === 1 && premiseIdReady && dailyRange.from && dailyRange.to) {
      const from = formatReadingsDate(dailyRange.from)
      const to = formatReadingsDate(dailyRange.to)
      const defSort = defaultSortByMap[filtersTypes.daily]
      const defOrder = defaultSortOrderMap[filtersTypes.daily]
      fetchDailyData(from, to, 1, undefined, defSort, defOrder)
    }
  }, [value, premiseIdReady])

  // Fetch periodic data from API using premiseId (joins through premise_meters)
  const fetchPeriodicData = useCallback(async (from?: string, to?: string, requestedPeriodOption?: PeriodOption, page: number = 1, sizeOverride?: number, sortByOverride?: string, sortOrderOverride?: 'ASC' | 'DESC') => {
    const premiseIdForApi = premiseIdRef.current
    if (!premiseIdForApi) {
      console.warn(
        '[PERIODIC DEBUG] Request was not sent because premiseId is missing.',
        { accountId, meterNumber: consumerId }
      )
      return
    }

    const effectivePeriodOption = requestedPeriodOption ?? periodicOption

    setPeriodicLoading(true)
    try {
      if (import.meta.env.DEV) {
        debugLog('[PERIODIC DEBUG] Sending request:', {
          accountId,
          premiseId: premiseIdForApi,
          meterNumber: consumerId,
          from: from ?? formatReadingsDate(periodicRange.from),
          to: to ?? formatReadingsDate(periodicRange.to),
          periodOption: effectivePeriodOption,
          page,
          size: sizeOverride ?? pageSize,
        })
      }

      const result = await getPeriodicReadings(accountId, premiseIdForApi, {
        page,
        size: sizeOverride ?? pageSize,
        from: from ?? formatReadingsDate(periodicRange.from),
        to: to ?? formatReadingsDate(periodicRange.to),
        periodOption: effectivePeriodOption,
        sortBy: getValidSortBy(filtersTypes.periodic, sortByOverride ?? sortBy),
        sortOrder: sortOrderOverride ?? sortOrder,
        meterNumber: consumerId,
      })

      if (import.meta.env.DEV) {
        debugLog('[PERIODIC DEBUG] Normalized frontend result:', {
          rows: result.rows.length,
          total: result.total,
          totalConsumption: result.totalConsumption,
          periodOption: result.periodOption,
          chartCurrentYearPoints: result.chart.currentYear.length,
          chartYearAgoPoints: result.chart.yearAgo?.length ?? 0,
          firstRows: result.rows.slice(0, 5),
        })
      }

      setPeriodicData(result.rows)
      setPeriodicRowCount(result.total)
      setPeriodicPage(page - 1) // DataGrid uses 0-based pages
      setPeriodicTotal(result.totalConsumption)
      setPeriodicAvg(result.rows.length > 0 ? result.totalConsumption / result.rows.length : 0)
      setPeriodicChart(result.chart)

    } catch (error) {
      console.error('[ConsumerCalls] Error fetching periodic data:', error)
    } finally {
      setPeriodicLoading(false)
    }
  }, [accountId, consumerId, pageSize, sortBy, sortOrder, periodicRange, periodicOption])

  // Auto-fetch periodic data when switching to periodic tab
  useEffect(() => {
    if (value === 2 && premiseIdReady && periodicRange.from && periodicRange.to) {
      const from = formatReadingsDate(periodicRange.from)
      const to = formatReadingsDate(periodicRange.to)
      const defSort = defaultSortByMap[filtersTypes.periodic]
      const defOrder = defaultSortOrderMap[filtersTypes.periodic]
      fetchPeriodicData(
        from,
        to,
        'BI_MONTHLY',
        1,
        undefined,
        defSort,
        defOrder
      )
    }
  }, [value, premiseIdReady])

  // Fetch detailed data from API using premiseId (joins through premise_meters)
  const fetchDetailedData = useCallback(async (from?: string, to?: string, page: number = 1, sizeOverride?: number, sortByOverride?: string, sortOrderOverride?: 'ASC' | 'DESC') => {
    const premiseIdForApi = premiseIdRef.current
    if (!premiseIdForApi) return

    setDetailedLoading(true)
    try {
      const result = await getDetailedReadings(accountId, premiseIdForApi, {
        page,
        size: sizeOverride ?? pageSize,
        from: from ?? formatReadingsDate(detailedRange.from),
        to: to ?? formatReadingsDate(detailedRange.to),
        sortBy: getValidSortBy(filtersTypes.detailed, sortByOverride ?? sortBy),
        sortOrder: sortOrderOverride ?? sortOrder,
      })

      setDetailedData(result.rows)
      setDetailedRowCount(result.total)
      setDetailedPage(page - 1) // DataGrid uses 0-based pages
    } catch (error) {
      console.error('[ConsumerCalls] Error fetching detailed data:', error)
    } finally {
      setDetailedLoading(false)
    }
  }, [accountId, consumerId, pageSize, sortBy, sortOrder, detailedRange])

  // Auto-fetch detailed data when switching to detailed tab
  useEffect(() => {
    if (value === 3 && premiseIdReady && detailedRange.from && detailedRange.to) {
      const from = formatReadingsDate(detailedRange.from)
      const to  =  formatReadingsDate(detailedRange.to)
      const defSort = defaultSortByMap[filtersTypes.detailed]
      const defOrder = defaultSortOrderMap[filtersTypes.detailed]
      fetchDetailedData(
        from,
        to,
        1,
        undefined,
        defSort,
        defOrder
      )
    }
  }, [value, premiseIdReady])

  // Reset sort to the correct backend defaults when switching tabs
  useEffect(() => {
    const tabType = options[value].type
    setSortBy(defaultSortByMap[tabType])
    setSortOrder(defaultSortOrderMap[tabType])
    setActiveSortField(tabType === filtersTypes.periodic ? 'period' : 'timestamp')
  }, [value])

  const handleHourlyPageChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize)
    }
    fetchHourlyData(undefined, undefined, newPage + 1, newPageSize)
  }, [fetchHourlyData, pageSize])

  const handleDailyPageChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) setPageSize(newPageSize)
    fetchDailyData(undefined, undefined, newPage + 1, newPageSize)
  }, [fetchDailyData, pageSize])

  const handlePeriodicPageChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) setPageSize(newPageSize)
    fetchPeriodicData(undefined, undefined, undefined, newPage + 1, newPageSize)
  }, [fetchPeriodicData, pageSize])

  const handleDetailedPageChange = useCallback((newPage: number, newPageSize?: number) => {
    if (newPageSize && newPageSize !== pageSize) setPageSize(newPageSize)
    fetchDetailedData(undefined, undefined, newPage + 1, newPageSize)
  }, [fetchDetailedData, pageSize])

  // Valid backend sortBy fields per tab (must match ConsumerFilter.java)
  const validSortFields: Record<string, Set<string>> = {
    [filtersTypes.hourly]: new Set(['hourly_meter_id', 'hourly_time', 'hourly_start_reading', 'hourly_start_reverse_reading', 'hourly_consumption']),
    [filtersTypes.daily]: new Set(['daily_meter_id', 'daily_day', 'daily_consumption', 'daily_start_reading', 'nightly_consumption']),
    [filtersTypes.periodic]: new Set(['period_start', 'period_consumption']),
    [filtersTypes.detailed]: new Set(['detailed_meter_id', 'detailed_reading_at', 'detailed_value', 'detailed_reverse']),
  }

  // Get a validated sortBy for a given tab type — falls back to default if the current value is invalid
  const getValidSortBy = (tabType: string, candidate: string | undefined): string => {
    if (candidate && validSortFields[tabType]?.has(candidate)) return candidate
    return defaultSortByMap[tabType]
  }

  // Map frontend DataGrid field names to backend sortBy names (from ConsumerFilter.java)
  const fieldToBackendMap: Record<string, Record<string, string>> = {
    [filtersTypes.hourly]: {
      meterNumber: 'hourly_meter_id',
      timestamp: 'hourly_time',
      value: 'hourly_start_reading',
      consumption: 'hourly_consumption',
    },
    [filtersTypes.daily]: {
      meterNumber: 'daily_meter_id',
      timestamp: 'daily_day',
      midnightCapacity: 'daily_start_reading',
      consumption: 'daily_consumption',
      nightConsumption: 'nightly_consumption',
    },
    [filtersTypes.periodic]: {
      period: 'period_start',
      consumption: 'period_consumption',
    },
    [filtersTypes.detailed]: {
      meterNumber: 'detailed_meter_id',
      timestamp: 'detailed_reading_at',
      value: 'detailed_value',
      consumption: 'detailed_reverse',
    },
  }


  // Compute the GridSortModel from current sort state (using frontend field names)
  const currentSortModel = useMemo(() => {
    if (!activeSortField) return []
    return [{ field: activeSortField, sort: sortOrder.toLowerCase() as 'asc' | 'desc' }]
  }, [activeSortField, sortOrder])

  const handleSortChange = useCallback((field: string, order: 'ASC' | 'DESC') => {
    const currentType = options[value].type
    setActiveSortField(field)
    setSortOrder(order)

    const backendField = fieldToBackendMap[currentType]?.[field]
    if (!backendField) {
      // Client-only sort (e.g. dayOfWeek, alerts) - sorted in-memory by DataGrid/sortedDisplayRows
      return
    }

    setSortBy(backendField)

    // Directly re-fetch with the new sort params since setState is async.
    // The fetch functions will pick up sort from their next useCallback cycle,
    // but we need an immediate fetch here, so we call the service directly.
    const idForApi = premiseIdRef.current
    if (!idForApi || !premiseIdReady) return

    if (currentType === filtersTypes.hourly) {
      setHourlyLoading(true)
      getHourlyReadings(accountId, idForApi, {
        page: 1, size: pageSize,
        from: formatReadingsDate(hourlyRange.from),
        to: formatReadingsDate(hourlyRange.to),
        sortBy: backendField, sortOrder: order,
      }).then(result => showHourlyResult(result, 1))
        .finally(() => setHourlyLoading(false))
    } else if (currentType === filtersTypes.daily) {
      setDailyLoading(true)
      getDailyReadings(accountId, idForApi, {
        page: 1, size: pageSize,
        from: formatReadingsDate(dailyRange.from),
        to: formatReadingsDate(dailyRange.to),
        sortBy: backendField, sortOrder: order,
      }).then(result => showDailyResult(result, 1))
        .finally(() => setDailyLoading(false))
    } else if (currentType === filtersTypes.periodic) {
      setPeriodicLoading(true)
      getPeriodicReadings(accountId, idForApi, {
        page: 1, size: pageSize,
        from: formatReadingsDate(periodicRange.from),
        to: formatReadingsDate(periodicRange.to),
        periodOption: periodicOption,
        sortBy: backendField, sortOrder: order,
        meterNumber: consumerId,
      }).then(result => {
        setPeriodicData(result.rows)
        setPeriodicRowCount(result.total)
        setPeriodicPage(0)
        setPeriodicTotal(result.totalConsumption)
        setPeriodicAvg(result.rows.length > 0 ? result.totalConsumption / result.rows.length : 0)
        setPeriodicChart(result.chart)
      }).finally(() => setPeriodicLoading(false))
    } else if (currentType === filtersTypes.detailed) {
      setDetailedLoading(true)
      getDetailedReadings(accountId, idForApi, {
        page: 1, size: pageSize,
        from: formatReadingsDate(detailedRange.from),
        to: formatReadingsDate(detailedRange.to),
        sortBy: backendField, sortOrder: order,
      }).then(result => {
        setDetailedData(result.rows)
        setDetailedRowCount(result.total)
        setDetailedPage(0)
      }).finally(() => setDetailedLoading(false))
    }
  }, [value, accountId, consumerId, pageSize, premiseIdReady, showHourlyResult, showDailyResult])

  const handleHourlySearch = useCallback((from?: string, to?: string) => {
    setHourlyRange({ from: parseApiDate(from) ?? hourlyRange.from, to: parseApiDate(to) ?? hourlyRange.to })
    return fetchHourlyData(from, to)
  }, [fetchHourlyData, hourlyRange])

  const handleDailySearch = useCallback((from?: string, to?: string) => {
    setDailyRange({ from: parseApiDate(from) ?? dailyRange.from, to: parseApiDate(to) ?? dailyRange.to })
    return fetchDailyData(from, to)
  }, [fetchDailyData, dailyRange])

  const handlePeriodicSearch = useCallback((from?: string, to?: string, selectedPeriodOption?: PeriodOption) => {
    setPeriodicRange({ from: parseApiDate(from) ?? periodicRange.from, to: parseApiDate(to) ?? periodicRange.to })
    setPeriodicOption(selectedPeriodOption ?? 'BI_MONTHLY')
    return fetchPeriodicData(from, to, selectedPeriodOption)
  }, [fetchPeriodicData, periodicRange])

  const readingsFailures: Partial<
    Record<filtersTypes, { title: string; error: GatewayError | null; loading: boolean; retry: () => void }>
  > = {
    [filtersTypes.hourly]: {
      title: 'הקריאות השעתיות לא נטענו',
      error: hourlyError,
      loading: hourlyLoading,
      retry: () => fetchHourlyData(),
    },
    [filtersTypes.daily]: {
      title: 'הקריאות היומיות לא נטענו',
      error: dailyError,
      loading: dailyLoading,
      retry: () => fetchDailyData(),
    },
  }
  const readingsFailure = readingsFailures[options[value].type]
  const premiseFailed = premise.status === 'error'

  return (
    <Box sx={{ width: '100%', minWidth: 0, height: 'calc(100dvh - 170px)', minHeight: 520, display: 'flex', flexDirection: 'column' }}>
      {!premiseFailed && (
        <Box sx={{
          borderBottom: 1,
          borderColor: isDarkMode ? '#445876' : 'divider',
          marginTop: '20px',
          width: '100%',
          minWidth: 0,
        }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
            sx={{
              direction: 'rtl',
              '& .MuiTabs-indicator': {
                backgroundColor: isDarkMode ? '#81B5FF' : '#323B771A',
                height: '3px',
              },
            }}
          >
            {options.map(option => (
              <Tab
                sx={{
                  outline: 'none !important',
                  color: isDarkMode ? '#fff !important' : '#003F61 !important',
                  '&.Mui-selected': {
                    color: isDarkMode ? '#81B5FF !important' : '#003F61 !important'
                  }
                }}
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Box sx={{ paddingTop: 'clamp(12px, 2vw, 30px)', width: '100%', minWidth: 0, minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {premiseFailed ? (
          <PageStateMessage
            tone="error"
            title={
              consumerId
                ? `לא ניתן לטעון קריאות עבור מד ${consumerId}`
                : 'לא ניתן לטעון קריאות'
            }
            detail={describeGatewayError(premise.error, 'טעינת פרטי המד נכשלה')}
            onRetry={premise.reload}
          />
        ) : (
          <ConsumerCallsContent
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            type={options[value].type}
            gridNotice={
              readingsFailure?.error && !readingsFailure.loading ? (
                <PageStateMessage
                  tone="error"
                  title={readingsFailure.title}
                  detail={describeGatewayError(readingsFailure.error, READINGS_FETCH_FAILED)}
                  onRetry={readingsFailure.retry}
                />
              ) : null
            }
            onSortChange={handleSortChange}
            sortModel={currentSortModel}
            hourlyData={hourlyData}
            dailyData={dailyData}
            dailyChartData={dailyChartData}
            hourlyLoading={hourlyLoading || premiseLoading}
            hourlyTotal={hourlyTotal}
            hourlyAvg={hourlyAvg}
            onHourlySearch={handleHourlySearch}
            hourlyRange={hourlyRange}
            onHourlyRangeChange={setHourlyRange}
            hourlyRowCount={hourlyRowCount}
            hourlyPage={hourlyPage}
            onHourlyPageChange={handleHourlyPageChange}
            dailyLoading={dailyLoading || premiseLoading}
            dailyChartLoading={dailyChartLoading}
            dailyChartError={dailyChartError}
            dailyTotal={dailyTotal}
            dailyAvg={dailyAvg}
            dailyNightlyTotal={dailyNightlyTotal}
            dailyNightlyAvg={dailyNightlyAvg}
            dailyMin={dailyMinInfo}
            dailyMax={dailyMaxInfo}
            onDailySearch={handleDailySearch}
            dailyRange={dailyRange}
            onDailyRangeChange={setDailyRange}
            onDailyChartWeekChange={fetchDailyChartWeek}
            dailyRowCount={dailyRowCount}
            dailyPage={dailyPage}
            onDailyPageChange={handleDailyPageChange}
            periodicData={periodicData}
            periodicLoading={periodicLoading || premiseLoading}
            periodicTotal={periodicTotal}
            periodicAvg={periodicAvg}
            periodicChart={periodicChart}
            onPeriodicSearch={handlePeriodicSearch}
            periodicRange={periodicRange}
            onPeriodicRangeChange={setPeriodicRange}
            periodicRowCount={periodicRowCount}
            periodicPage={periodicPage}
            onPeriodicPageChange={handlePeriodicPageChange}
            detailedData={detailedData}
            detailedLoading={detailedLoading || premiseLoading}
            onDetailedSearch={fetchDetailedData}
            detailedRange={detailedRange}
            onDetailedRangeChange={setDetailedRange}
            detailedRowCount={detailedRowCount}
            detailedPage={detailedPage}
            onDetailedPageChange={handleDetailedPageChange}
          />
        )}
      </Box>

      {/* Export progress indicator */}
      <Snackbar
        open={isExporting}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: isDarkMode ? '#1D325A' : '#fff',
            color: isDarkMode ? '#fff' : '#003F61',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 300,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                מייצא קובץ Excel...
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'left' }}>
                {exportProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{
                borderRadius: 1,
                height: 6,
                bgcolor: isDarkMode ? '#374151' : '#E4E7EB',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#81B5FF',
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        </Box>
      </Snackbar>
    </Box>
  )
}

export default ConsumerCalls
